import { Decimal } from '@snap-types/types/decimal';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { getQuoteLineOrThrow } from '../utilities/action-guards';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

type ValidateQuotePriceQuantitiesParams = {
  quantity: Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

export function validateDuplicateQuotePriceQuantities(
  state: WritableQuoteBuilderStateType,
  {
    quantity,
    quoteLineUuid,
    quotePriceUuid,
  }: ValidateQuotePriceQuantitiesParams,
) {
  const issueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    field: 'quantity',
    type: 'duplication',
  });

  // Delete the current issue and clean up sibling/parent issues if they are resolved.
  deleteIssue(state, issueId);

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  const duplicateQuantityIds: string[] = [];

  // Find all quote prices with the same quantity
  for (const qpUuid of quoteLine.quotePriceUuids) {
    if (qpUuid === quotePriceUuid) {
      continue;
    }

    const qp = quoteLine.quotePrices[qpUuid];

    if (qp.quantity.equals(quantity)) {
      duplicateQuantityIds.push(qpUuid);
    }
  }

  if (duplicateQuantityIds.length) {
    const parentIssueId = buildIssueId({
      area: 'quoteLine',
      quoteLineUuid,
      type: 'duplication',
    });

    // If there are any duplicates, create the parent issue
    upsertIssue(state, parentIssueId, {
      type: 'duplication',
      area: 'quoteLine',
      title: 'Duplicate Quantity',
      description: 'Multiple quote prices have the same quantity',
      quoteLineUuid,
      childIssueIds: [
        ...duplicateQuantityIds.map((qpUuid) =>
          buildIssueId({
            area: 'quotePrice',
            quoteLineUuid,
            quotePriceUuid: qpUuid,
            type: 'duplication',
            field: 'quantity',
          }),
        ),
        issueId,
      ],
      severity: 'error',
      action: {
        type: 'display',
      },
    });

    // And create the current issue
    upsertIssue(state, issueId, {
      type: 'duplication',
      area: 'quotePrice',
      title: 'Duplicate Quantity',
      description: 'Duplicate quantities detected',
      quoteLineUuid,
      quotePriceUuid,
      parentIssueId,
      siblingIssueIds: duplicateQuantityIds.map((qpUuid) =>
        buildIssueId({
          area: 'quotePrice',
          quoteLineUuid,
          quotePriceUuid: qpUuid,
          type: 'duplication',
          field: 'quantity',
        }),
      ),
      severity: 'error',
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'quantity',
        }),
      },
    });

    // Finally, map through the duplicate quantity issues and create them
    for (const qpUuid of duplicateQuantityIds) {
      const qpIssueId = buildIssueId({
        area: 'quotePrice',
        quoteLineUuid,
        quotePriceUuid: qpUuid,
        type: 'duplication',
        field: 'quantity',
      });

      upsertIssue(state, qpIssueId, {
        type: 'duplication',
        area: 'quotePrice',
        title: 'Duplicate Quantity',
        description: 'Duplicate quantities detected',
        quoteLineUuid,
        quotePriceUuid: qpUuid,
        parentIssueId,
        siblingIssueIds: [
          ...duplicateQuantityIds
            .filter((qpId) => qpId !== qpUuid)
            .map((qpUuid) =>
              buildIssueId({
                area: 'quotePrice',
                quoteLineUuid,
                quotePriceUuid: qpUuid,
                type: 'duplication',
                field: 'quantity',
              }),
            ),
          issueId,
        ],
        severity: 'error',
        action: {
          type: 'modify',
          elementId: buildElementId({
            uuid: qpUuid,
            field: 'quantity',
          }),
        },
      });
    }
  }
}
